import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import RFMActions from 'store/analyticsStores/RFMStore/RFMActions';
import { computePercent } from 'helpers';
import { ERROR, SUCCESS} from '../../../../store/globalStatuses/globalStatuses';
import Modal from 'portals/Modal';
import ButtonFilled from '../../../../components/FormElements/ButtonFilled';
import Loader from '../../../../components/Loader';
import styles from './rmfAnalysis.module.scss';
import { useCurrentBranch } from "../../../../hooks/useCurrentBranch";
import { Roles } from "../../../../config";
import Invis from "../../../../components/Filter/Invis";
import { convertDataToTransactions } from "../../../../tools/convertDateToString";
import VideoModal from "../../../../components/VideoModal";
import YOUTUBE_LINKS from "../../../../config/youtubeLinks";

// New segment breakdown mapping (rows: R1M1..R3M3, cols: F3, F2, F1)
const cells = [
  ['EMERGING_CUSTOMERS', 'TOP_CUSTOMERS',          'TOP_CUSTOMERS'],           // R1M1: 131,121,111
  ['EMERGING_CUSTOMERS', 'ACTIVE_CUSTOMERS',        'TOP_CUSTOMERS'],           // R1M2: 132,122,112
  ['EMERGING_CUSTOMERS', 'ACTIVE_CUSTOMERS',        'ACTIVE_CUSTOMERS'],        // R1M3: 133,123,113
  ['UNSTEADY_CUSTOMERS', 'UNSTEADY_CUSTOMERS',      'POTENTIAL_LOST_CUSTOMERS'],// R2M1: 231,221,211
  ['UNSTEADY_CUSTOMERS', 'UNSTEADY_CUSTOMERS',      'UNSTEADY_CUSTOMERS'],      // R2M2: 232,222,212
  ['POTENTIAL_LOST_CUSTOMERS', 'UNSTEADY_CUSTOMERS','UNSTEADY_CUSTOMERS'],      // R2M3: 233,223,213
  ['POTENTIAL_LOST_CUSTOMERS', 'POTENTIAL_LOST_CUSTOMERS', 'POTENTIAL_LOST_CUSTOMERS'], // R3M1: 331,321,311
  ['LOST_CUSTOMERS',      'INACTIVE_CUSTOMERS',     'INACTIVE_CUSTOMERS'],      // R3M2: 332,322,312
  ['LOST_CUSTOMERS',      'LOST_CUSTOMERS',         'INACTIVE_CUSTOMERS'],      // R3M3: 333,323,313
];

const SEGMENT_CLASS_MAP = {
  TOP_CUSTOMERS:           'topCustomer',
  ACTIVE_CUSTOMERS:        'activeCustomer',
  EMERGING_CUSTOMERS:      'emergingCustomer',
  UNSTEADY_CUSTOMERS:      'unsteadyCustomer',
  POTENTIAL_LOST_CUSTOMERS:'potentialLostCustomer',
  INACTIVE_CUSTOMERS:      'inactiveCustomer',
  LOST_CUSTOMERS:          'lostCustomer',
};

const SEGMENT_KEYS = [
  'TOP_CUSTOMERS',
  'ACTIVE_CUSTOMERS',
  'EMERGING_CUSTOMERS',
  'UNSTEADY_CUSTOMERS',
  'POTENTIAL_LOST_CUSTOMERS',
  'INACTIVE_CUSTOMERS',
  'LOST_CUSTOMERS',
];

// Recommendations config per segment
const RECOMMENDATIONS_CONFIG = {
  TOP_CUSTOMERS: [
    {
      nameKey: 'NSAnalysis.recActions.referral',
      descKey: 'NSAnalysis.recDescs.TOP_CUSTOMERS.referral',
      actions: [{ labelKey: 'NSAnalysis.recActions.goReferral', path: '/profile/notifications/referral' }],
    },
    {
      nameKey: 'NSAnalysis.recActions.tagsAndGradation',
      descKey: 'NSAnalysis.recDescs.TOP_CUSTOMERS.tagsAndGradation',
      actions: [
        { labelKey: 'NSAnalysis.recActions.goTags', path: '/profile/tags' },
        { labelKey: 'NSAnalysis.recActions.goGradation', path: '/profile/bonus/gradation' },
      ],
    },
    {
      nameKey: 'NSAnalysis.recActions.certificates',
      descKey: 'NSAnalysis.recDescs.TOP_CUSTOMERS.certificates',
      actions: [{ labelKey: 'NSAnalysis.recActions.goCertificates', path: '/profile/certificate/list' }],
    },
    {
      nameKey: 'NSAnalysis.recActions.notification',
      descKey: 'NSAnalysis.recDescs.TOP_CUSTOMERS.notification',
      actions: [{ labelKey: 'NSAnalysis.recActions.goNotification', path: '/rfm/analytics/TOP_CUSTOMERS/send-notification' }],
    },
  ],
  ACTIVE_CUSTOMERS: [
    {
      nameKey: 'NSAnalysis.recActions.stamps',
      descKey: 'NSAnalysis.recDescs.ACTIVE_CUSTOMERS.stamps',
      actions: [{ labelKey: 'NSAnalysis.recActions.goStamps', path: '/profile/prizes' }],
    },
    {
      nameKey: 'NSAnalysis.recActions.tagsAndGradation',
      descKey: 'NSAnalysis.recDescs.ACTIVE_CUSTOMERS.tagsAndGradation',
      actions: [
        { labelKey: 'NSAnalysis.recActions.goTags', path: '/profile/tags' },
        { labelKey: 'NSAnalysis.recActions.goGradation', path: '/profile/bonus/gradation' },
      ],
    },
    {
      nameKey: 'NSAnalysis.recActions.referral',
      descKey: 'NSAnalysis.recDescs.ACTIVE_CUSTOMERS.referral',
      actions: [{ labelKey: 'NSAnalysis.recActions.goReferral', path: '/profile/notifications/referral' }],
    },
  ],
  EMERGING_CUSTOMERS: [
    {
      nameKey: 'NSAnalysis.recActions.firstTransaction',
      descKey: 'NSAnalysis.recDescs.EMERGING_CUSTOMERS.firstTransaction',
      actions: [{ labelKey: 'NSAnalysis.recActions.goWhatsapp', path: '/profile/whatsapp-header' }],
    },
    {
      nameKey: 'NSAnalysis.recActions.stamps',
      descKey: 'NSAnalysis.recDescs.EMERGING_CUSTOMERS.stamps',
      actions: [{ labelKey: 'NSAnalysis.recActions.goStamps', path: '/profile/prizes' }],
    },
    {
      nameKey: 'NSAnalysis.recActions.notification',
      descKey: 'NSAnalysis.recDescs.EMERGING_CUSTOMERS.notification',
      actions: [{ labelKey: 'NSAnalysis.recActions.goNotification', path: '/rfm/analytics/EMERGING_CUSTOMERS/send-notification' }],
    },
  ],
  UNSTEADY_CUSTOMERS: [
    {
      nameKey: 'NSAnalysis.recActions.promoOffers',
      descKey: 'NSAnalysis.recDescs.UNSTEADY_CUSTOMERS.promoOffers',
      actions: [{ labelKey: 'NSAnalysis.recActions.goNotification', path: '/rfm/analytics/UNSTEADY_CUSTOMERS/send-notification' }],
    },
    {
      nameKey: 'NSAnalysis.recActions.delayedBonuses',
      descKey: 'NSAnalysis.recDescs.UNSTEADY_CUSTOMERS.delayedBonuses',
      actions: [{ labelKey: 'NSAnalysis.recActions.goBonus', path: '/profile/bonus/activate' }],
    },
    {
      nameKey: 'NSAnalysis.recActions.autoNotifications',
      descKey: 'NSAnalysis.recDescs.UNSTEADY_CUSTOMERS.autoNotifications',
      actions: [{ labelKey: 'NSAnalysis.recActions.goAutomailing', path: '/profile/bonus/automailing' }],
    },
    {
      nameKey: 'NSAnalysis.recActions.stamps',
      descKey: 'NSAnalysis.recDescs.UNSTEADY_CUSTOMERS.stamps',
      actions: [{ labelKey: 'NSAnalysis.recActions.goStamps', path: '/profile/prizes' }],
    },
  ],
  POTENTIAL_LOST_CUSTOMERS: [
    {
      nameKey: 'NSAnalysis.recActions.certificates',
      descKey: 'NSAnalysis.recDescs.POTENTIAL_LOST_CUSTOMERS.certificates',
      actions: [{ labelKey: 'NSAnalysis.recActions.goCertificates', path: '/profile/certificate/list' }],
    },
    {
      nameKey: 'NSAnalysis.recActions.referral',
      descKey: 'NSAnalysis.recDescs.POTENTIAL_LOST_CUSTOMERS.referral',
      actions: [{ labelKey: 'NSAnalysis.recActions.goReferral', path: '/profile/notifications/referral' }],
    },
    {
      nameKey: 'NSAnalysis.recActions.notification',
      descKey: 'NSAnalysis.recDescs.POTENTIAL_LOST_CUSTOMERS.notification',
      actions: [{ labelKey: 'NSAnalysis.recActions.goNotification', path: '/rfm/analytics/POTENTIAL_LOST_CUSTOMERS/send-notification' }],
    },
    {
      nameKey: 'NSAnalysis.recActions.tags',
      descKey: 'NSAnalysis.recDescs.POTENTIAL_LOST_CUSTOMERS.tags',
      actions: [{ labelKey: 'NSAnalysis.recActions.goTags', path: '/profile/tags' }],
    },
    {
      nameKey: 'NSAnalysis.recActions.review',
      descKey: 'NSAnalysis.recDescs.POTENTIAL_LOST_CUSTOMERS.review',
      actions: [{ labelKey: 'NSAnalysis.recActions.goReview', path: '/profile/review-request' }],
    },
  ],
  INACTIVE_CUSTOMERS: [
    {
      nameKey: 'NSAnalysis.recActions.certificates',
      descKey: 'NSAnalysis.recDescs.INACTIVE_CUSTOMERS.certificates',
      actions: [{ labelKey: 'NSAnalysis.recActions.goCertificates', path: '/profile/certificate/list' }],
    },
    {
      nameKey: 'NSAnalysis.recActions.notification',
      descKey: 'NSAnalysis.recDescs.INACTIVE_CUSTOMERS.notification',
      actions: [{ labelKey: 'NSAnalysis.recActions.goNotification', path: '/rfm/analytics/INACTIVE_CUSTOMERS/send-notification' }],
    },
  ],
  LOST_CUSTOMERS: [
    {
      nameKey: 'NSAnalysis.recActions.notification',
      descKey: 'NSAnalysis.recDescs.LOST_CUSTOMERS.notification',
      actions: [{ labelKey: 'NSAnalysis.recActions.goNotification', path: '/rfm/analytics/LOST_CUSTOMERS/send-notification' }],
    },
    {
      nameKey: 'NSAnalysis.recActions.certificates',
      descKey: 'NSAnalysis.recDescs.LOST_CUSTOMERS.certificates',
      actions: [{ labelKey: 'NSAnalysis.recActions.goCertificates', path: '/profile/certificate/list' }],
    },
    {
      nameKey: 'NSAnalysis.recActions.crossMarketing',
      descKey: 'NSAnalysis.recDescs.LOST_CUSTOMERS.crossMarketing',
      actions: [{ labelKey: 'NSAnalysis.recActions.goCrossMarketing', path: '/cross-marketing/' }],
    },
  ],
};

const initialState = {
  TOP_CUSTOMERS: false,
  ACTIVE_CUSTOMERS: false,
  EMERGING_CUSTOMERS: false,
  UNSTEADY_CUSTOMERS: false,
  POTENTIAL_LOST_CUSTOMERS: false,
  INACTIVE_CUSTOMERS: false,
  LOST_CUSTOMERS: false,
};

export default function RFMAnalysis() {
  const dispatch = useDispatch();
  const history = useHistory();
  const branch = useSelector(s => s.branchReducer.info);
  const rfm = useSelector(s => s.RFMReducer);
  const { t } = useTranslation();

  const invisParams = useSelector(state => state.filtersReducer)

  const [customerTypes, setCustomerTypes] = useState(initialState);
  const [modal, setModal] = useState(false);
  const [errorModal, setErrorModal] = useState(false);
  const [pending, setPending] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [activeTab, setActiveTab] = useState('analytics');
  const { currentRole } = useCurrentBranch()

  const exportExcel = () => {
    setDownloading(true);
    RFMActions.getRFMAnalyticExport(branch.group_id, invisParams)
      .catch((e) => {
        console.error('RFM export error:', e);
        alert(e.message || t("exportError"));
      })
      .finally(() => setDownloading(false));
  }

  useEffect(() => {
    if (branch) {
      new Promise((resolve, reject) => {
        setPending(true);
        dispatch(RFMActions.getRFM(branch.id, currentRole, invisParams, branch.group_id, resolve, reject))
      })
        .catch((e) => {
          if (e?.response?.data?.message === "you didn't fill in the indexes for rfm analysis") {
            setModal(true);
          } else {
            setErrorModal(true);
          }
        })
        .finally(() => setPending(false));
    }
  }, [branch, invisParams]);

  const onMouseIn = (type) => {
    setCustomerTypes({...initialState, [type]: true});
  }

  const onMouseOut = (type) => {
    setCustomerTypes({...customerTypes, [type]: false});
  }

  const onCloseModal = () => {
    setModal(false);
  }

  const onOpenModal = () => {
    setModal(true);
  }

  const goToClient = (clientType) => () => {
    history.push(`/analytics/rfm/${clientType.toLowerCase()}`)
  }

  const navigate = (path) => () => {
    history.push(path);
  }

  const renderTableCells = () => {
    const shownSegments = new Set();
    return cells.flatMap((row, rowIndex) =>
      row.map((segment, colIndex) => {
        const isFirst = !shownSegments.has(segment);
        shownSegments.add(segment);
        const segClass = SEGMENT_CLASS_MAP[segment];
        return (
          <div
            key={`${rowIndex}-${colIndex}`}
            className={`${styles.customer} ${styles[segClass]} ${customerTypes[segment] ? styles.active : ''}`}
            onMouseOver={() => onMouseIn(segment)}
            onMouseLeave={() => onMouseOut(segment)}
            onClick={goToClient(segment)}
          >
            {isFirst && rfm[segment] && (
              <span className={styles.segmentLabel}>
                {t(`NSAnalysis.${segment}`).replace(/\s*\(.*\)\s*$/, '')} {computePercent(rfm[segment].length, rfm.ALL_CUSTOMERS.length)}%
              </span>
            )}
          </div>
        );
      })
    );
  };

  const renderRFM = () => {
    return (
      <>
        <table className={styles.table}>
          <thead>
          <tr>
            <th>
              <div className={styles.tableHead}>
                <div>r</div>
              </div>
            </th>
            <th>
              <div className={styles.tableHead}>
                <div>m</div>
              </div>
            </th>
            <th>
              <div className={styles.tableHead}>
                <div>f-3</div>
                <div>f-2</div>
                <div>f-1</div>
              </div>
            </th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <td>
              <div>1</div>
              <div>2</div>
              <div>3</div>
            </td>
            <td>
              {new Array(3).fill(new Array(3).fill('')).map((_, index) => _.map((item, i) => (
                <div key={index + '' + i}>{i+1}</div>
              )))}
            </td>
            <td>
              <div className={styles.tableF}>
                {renderTableCells()}
              </div>
            </td>
          </tr>
          </tbody>
        </table>
        <div className={styles.info}>
          {SEGMENT_KEYS.map(seg => rfm[seg] && rfm[seg].length ? (
            <div
              key={seg}
              className={styles.infoItem}
              onMouseOver={() => onMouseIn(seg)}
              onMouseLeave={() => onMouseOut(seg)}
            >
              <div className={`${styles.infoRound} ${styles[SEGMENT_CLASS_MAP[seg]]}`}></div>
              <div className={styles.infoText}>{t(`NSAnalysis.${seg}`)}</div>
            </div>
          ) : null)}
        </div>
      </>
    )
  }

  const renderRecommendations = () => {
    const sortedSegments = SEGMENT_KEYS
      .filter(seg => rfm[seg] && rfm[seg].length > 0)
      .sort((a, b) => rfm[b].length - rfm[a].length);

    if (sortedSegments.length === 0) {
      return <div className={styles.error}><h2>{t('noData')}</h2></div>;
    }

    return (
      <div className={styles.recommendations}>
        {sortedSegments.map(seg => (
          <div key={seg} className={`${styles.recBlock} ${styles[SEGMENT_CLASS_MAP[seg] + 'Block']}`}>
            <div className={`${styles.recHeader} ${styles[SEGMENT_CLASS_MAP[seg]]}`}>
              <span className={styles.recSegmentName}>{t(`NSAnalysis.${seg}`)}</span>
              <span className={styles.recCount}>
                {rfm[seg].length} ({computePercent(rfm[seg].length, rfm.ALL_CUSTOMERS.length)}%)
              </span>
            </div>
            <div className={styles.recGoal}>
              <span className={styles.recGoalLabel}>{t('NSAnalysis.goal')}:</span> {t(`NSAnalysis.goals.${seg}`)}
            </div>
            <div className={styles.recItems}>
              {RECOMMENDATIONS_CONFIG[seg].map((rec, idx) => (
                <div key={idx} className={styles.recItem}>
                  <div className={styles.recItemNumber}>{idx + 1}</div>
                  <div className={styles.recItemContent}>
                    <div className={styles.recItemName}>{t(rec.nameKey)}</div>
                    <div className={styles.recItemDesc}>{t(rec.descKey)}</div>
                    <div className={styles.recItemActions}>
                      {rec.actions.map((action, aIdx) => (
                        <ButtonFilled key={aIdx} onClick={navigate(action.path)}>
                          {t(action.labelKey)}
                        </ButtonFilled>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
        <div className={styles.container}>
          <div className={styles.tabs}>
            <button
              className={`${styles.tab} ${activeTab === 'analytics' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('analytics')}
            >
              {t('NSAnalysis.tabs.analytics')}
            </button>
          </div>

          {activeTab === 'analytics' && (
            <div style={{ marginBottom: 24 }}>
              <VideoModal url={YOUTUBE_LINKS.AnalyticsRFM} />
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center' }}>
            {currentRole === Roles.ADMINISTRATOR ? (
              <div style={{marginLeft: 'auto', width: '100%', padding: '0px 10px 24px 0px'}}>
                <Invis />
              </div>
            ) : null}
            {branch?.group_id && activeTab === 'analytics' ? (
              <div style={{marginLeft: 'auto'}}>
                <ButtonFilled
                  onClick={exportExcel}
                  loading={downloading || pending}
                >{t("export")}</ButtonFilled>
              </div>
            ) : null}
          </div>

          {pending ? (
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', height: '500px'}}>
              <Loader color="#73D389"/>
            </div>
          ) : (
            <>
              {rfm.status === ERROR ? (
                <div className={styles.error}>
                  <h2>{t("pageIsNotAvailable")}</h2>
                </div>
              ) : activeTab === 'analytics' ? renderRFM() : renderRecommendations()}
            </>
          )}

          <Modal
            open={modal} size="small"
            onClickClose={onCloseModal}>
            <div className={styles.modal}>
              <p>
                {t("optionForbidden")} <br/>
                {t("touchModerator")}
              </p>
              <div>
                <ButtonFilled
                  onClick={() => history.push('/profile/rfm')}
                >{t("settings")}</ButtonFilled>
              </div>
            </div>
          </Modal>

          <Modal
            open={errorModal} size="small"
            onClickClose={() => setErrorModal(false)}>
            <div className={styles.modal}>
              <p>
                {t("optionForbidden")} <br/>
                {t("touchModerator")}
              </p>
              <div>
                <ButtonFilled
                  onClick={() => setErrorModal(false)}
                >{t("close")}</ButtonFilled>
              </div>
            </div>
          </Modal>
        </div>
    )
}
